import React from "react";

interface EvictionStats {
  topYears: { year: string; percent: number }[];
  dominantYear: string;
  dominantYearCount: number;
  yearOverYearChange: number;
  mostVolatileYear: string;
  mostVolatileChange: number;
  mostStableYear: string;
  mostStableChange: number;
  priorYear:number;
}

interface Props {
  evictionStats: EvictionStats;
}

const EvictionYearlyTrendsCard: React.FC<Props> = ({ evictionStats }) => {
  // Color definitions
  const colors = {
    primary: "#f8841c",       // Orange
    textDark: "#3A4A5F",      // Dark text color
    textLight: "#5D6D83",     // Light text color
    positive: "#6A9D7B",      // Green for positive trends
    negative: "#C37D7D",      // Red for negative trends
    dominant: "#89AEB2",      // Teal for dominant year
    volatile: "#8FA7C7",      // Soft blue for most volatile
    stable: "#BFD3C1"         // Light green for most stable
  };

  const isPositiveTrend = evictionStats.yearOverYearChange >= 0;
  const trendColor = isPositiveTrend ? colors.positive : colors.negative;
  const trendIcon = isPositiveTrend ? "↑" : "↓";

  // Using your specified color palette for the bars
  const colorPalette = [
    "#A7C7E7", "#BFD3C1", "#89AEB2", "#D3DCE3", 
    "#A5BFD6", "#C5D8E1", "#8FA7C7", "#E2E8F0",
    "#9DBAD5", "#B7C8DB", "#98AFC7", "#C0D3E0"
  ];

  return (
    <div style={{
      backgroundColor: "#F8FBFD",
      borderRadius: "10px",
      padding: "16px",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
      height: "420px",
      display: "flex",
      flexDirection: "column",
      border: "1px solid #D3DCE3",
      fontFamily: "'Segoe UI', 'Helvetica Neue', sans-serif"
    }}>
      {/* Header - updated with orange theme */}
      <div style={{ marginBottom: "16px" }}>
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          marginBottom: "4px"
        }}>
          <div style={{
            width: '24px',
            height: '24px',
            backgroundColor: colors.primary,
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2">
              <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
            </svg>
          </div>
          <h4 style={{
            color: colors.textDark,
            margin: 0,
            fontSize: '18px',
            fontWeight: 700,
            letterSpacing: '-0.25px'
          }}>
            Eviction Yearly Trends
          </h4>
        </div>
        <p style={{
          color: colors.textLight,
          margin: 0,
          fontSize: "12px",
          marginLeft: "34px" // Align with the text
        }}>
          Composition analysis by years.
        </p>
      </div>

      {/* Top Years */}
      <div style={{ marginBottom: "12px" }}>
        <div style={{
          display: "grid",
          gridTemplateColumns: "1fr auto",
          gap: "10px",
          color: colors.textLight,
          fontSize: "12px",
          fontWeight: 500,
          marginBottom: "6px"
        }}>
          <span>Top Years</span>
          <span>% of Total</span>
        </div>
        {evictionStats.topYears.map((item, index) => (
          <div key={index} style={{ 
            marginBottom: "6px",
            display: "grid",
            gridTemplateColumns: "1fr auto",
            gap: "10px",
            alignItems: "center"
          }}>
            <span style={{ 
              color: colors.textDark, 
              fontSize: "13px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis"
            }}>
              {item.year}
            </span>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span style={{ 
                color: colors.textDark, 
                fontSize: "13px", 
                fontWeight: 600,
                minWidth: "30px",
                textAlign: "right"
              }}>
                {item.percent}%
              </span>
              <div style={{
                width: "40px",
                height: "4px",
                backgroundColor: colorPalette[index % colorPalette.length],
                borderRadius: "2px"
              }} />
            </div>
          </div>
        ))}
      </div>

      {/* Metrics Container */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "10px",
        marginBottom: "12px",
        flex: 1
      }}>
        {/* Dominant Year - with original teal border */}
        <div style={{
          backgroundColor: "white",
          borderRadius: "6px",
          padding: "12px",
          borderLeft: `3px solid ${colors.dominant}`,
          display: "flex",
          flexDirection: "column"
        }}>
          <div style={{ 
            color: colors.primary,
            fontSize: "11px",
            marginBottom: "8px",
            fontWeight: 600
          }}>
            Dominant Year
          </div>
          <div style={{ 
            color: colors.textDark,
            fontSize: "16px",
            fontWeight: 700,
            marginBottom: "4px"
          }}>
            {evictionStats.dominantYear}
          </div>
          <div style={{ 
            color: colors.dominant,
            fontSize: "14px",
            fontWeight: 600
          }}>
            {evictionStats.dominantYearCount.toLocaleString()}
          </div>
        </div>

        {/* Yearly Trend - with trend color border */}
        <div style={{
          backgroundColor: "white",
          borderRadius: "6px",
          padding: "12px",
          borderLeft: `3px solid ${trendColor}`,
          display: "flex",
          flexDirection: "column"
        }}>
          <div style={{ 
            color: colors.primary,
            fontSize: "11px",
            marginBottom: "8px",
            fontWeight: 600
          }}>
            Current Trend
          </div>
          <div style={{ 
            color: trendColor,
            fontSize: "16px",
            fontWeight: 700,
            marginBottom: "4px",
            display: "flex",
            alignItems: "center",
            gap: "4px"
          }}>
            {trendIcon} {Math.abs(evictionStats.yearOverYearChange)}%
          </div>
          <div style={{ 
            color: colors.textLight,
            fontSize: "12px"
          }}>
            vs. {evictionStats.priorYear}
          </div>
        </div>
      </div>

      {/* Volatility Container */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "10px",
        marginTop: "auto"
      }}>
        {/* Most Volatile - with original soft blue border */}
        <div style={{
          backgroundColor: "white",
          borderRadius: "6px",
          padding: "10px",
          borderLeft: `3px solid ${colors.volatile}`
        }}>
          <div style={{ 
            color: colors.primary,
            fontSize: "11px",
            marginBottom: "6px",
            fontWeight: 600
          }}>
            Most Volatile
          </div>
          <div style={{ 
            color: colors.textDark,
            fontSize: "13px",
            fontWeight: 600,
            marginBottom: "2px"
          }}>
            {evictionStats.mostVolatileYear}
          </div>
          <div style={{ 
            color: colors.negative,
            fontSize: "12px"
          }}>
            ±{evictionStats.mostVolatileChange}% YoY
          </div>
        </div>

        {/* Most Stable - with original light green border */}
        <div style={{
          backgroundColor: "white",
          borderRadius: "6px",
          padding: "10px",
          borderLeft: `3px solid ${colors.stable}`
        }}>
          <div style={{ 
            color: colors.primary,
            fontSize: "11px",
            marginBottom: "6px",
            fontWeight: 600
          }}>
            Most Stable
          </div>
          <div style={{ 
            color: colors.textDark,
            fontSize: "13px",
            fontWeight: 600,
            marginBottom: "2px"
          }}>
            {evictionStats.mostStableYear}
          </div>
          <div style={{ 
            color: colors.positive,
            fontSize: "12px"
          }}>
            ±{evictionStats.mostStableChange}% YoY
          </div>
        </div>
      </div>

      {/* Footer */}
      <div style={{
        marginTop: "8px",
        color: colors.textLight,
        fontSize: "10px",
        textAlign: "right"
      }}>
        Updated: {new Date().toLocaleDateString()}
      </div>
    </div>
  );
};

export default EvictionYearlyTrendsCard;